import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { BehaviorSubject, combineLatest, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { formatUserName } from 'app/core/utilities/user-util';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import { BaseUser, ChatGetMessage, OutgoingMessageType, SocialEventType } from 'messages/websocket.messages';

import Message from '../message/message';
import {
  MultipleOptionsMessageType,
  SettingsMultipleOptionsMessage,
} from '../multiple-options-message/multiple-options-message-type';
import { SideContainerType } from '../social-menu/enums/SideContainerType';
import SocialStateMachine from '../social-state-machine/social-state-machine';
import { socialAppSelectors } from '../state/app';

import { HeaderMultipleOptionsMessageService } from './header-multiple-options-message.service';
import { HeaderToastMessageService } from './header-toast-message.service';
import { SideSectionService } from './side-section.service';
import { VideoChatService } from './videochat.service';
import { WebsocketService } from './websocket.service';
import { ChatService } from './chat.service';
import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { MODERATED_CHAT } from 'entity/SocialSettings';

enum AdmitOptions {
  ADMIT = 1,
  MANAGE,
}
@Injectable({
  providedIn: 'root',
})
export class HostService extends Loggable {
  private videoChatService = inject(VideoChatService);
  private headerToastMessageService = inject(HeaderToastMessageService);
  private headerMultipleOptionsMessageService = inject(HeaderMultipleOptionsMessageService);
  private sideSectionService = inject(SideSectionService);
  private websocketService = inject(WebsocketService);
  private chatService = inject(ChatService);
  private store = inject<Store<AppState>>(Store);

  public isHostChanged$ = new Subject<boolean>();
  public requestOpenGuestList$ = new BehaviorSubject<boolean>(false);

  private userId?: string;
  private event = false;
  private socialId?: string;
  private moderatedChat?: boolean;
  private chatModerationSetting?: boolean;

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    super();
    this.setDisplayName('HostService');
  }
  public init() {
    this.destroyed$ = new ReplaySubject<boolean>(1);

    this.store
      .select(coreSelectors.getUserId)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((userId) => (this.userId = userId));

    this.store
      .select(socialAppSelectors.getCurrentSocialId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((social) => (this.socialId = social));

    combineLatest([
      getFeatureFlagObservable(this.store, LaunchDarklyFlags.moderated_chat),
      this.store.select(socialAppSelectors.getRoomSettings),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([moderatedChat, socialSettings]) => {
        this.moderatedChat = moderatedChat;

        if (moderatedChat && socialSettings && socialSettings.setSettings) {
          this.chatModerationSetting = socialSettings.setSettings[MODERATED_CHAT] ? true : false;
        }
      });

    // Show host admit message
    this.store
      .select(socialAppSelectors.getWaitingUsers)
      .pipe(
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        takeUntil(this.destroyed$),
      )
      .subscribe((waitingUsers) => {
        switch (waitingUsers.length) {
          case 0:
            this.headerMultipleOptionsMessageService.removeMessage();
            break;
          case 1:
            this.sendSingleAdmitUserMessage(waitingUsers[0]);
            break;
          default:
            this.sendMultipleAdmitUserMessage(waitingUsers);
            break;
        }
      });

    this.store
      .select(socialAppSelectors.getSocialEvent)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event) => {
        this.event = event?.eventType === SocialEventType.CONVO || event?.eventType === SocialEventType.TOAST;
      });
  }

  public destroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private displayHostInfo(firstName: string, lastName: string, message: string): void {
    this.headerToastMessageService.pushTemporaryMessage(
      {
        message: `${firstName} ${lastName || ''} ${message}`,
        durationInSeconds: 7,
        buttonText: 'OKAY',
      },
      (id) => this.headerToastMessageService.removeMessage(id),
    );
  }

  private sendSingleAdmitUserMessage(waitingUser: BaseUser): void {
    const optionsList: SettingsMultipleOptionsMessage[] = [
      { text: 'ADMIT', color: '#39AD65' },
      { text: 'MANAGE', color: '#81B7E2' },
    ];
    const userName = formatUserName(waitingUser);
    this.headerMultipleOptionsMessageService
      .pushStaticMessage(
        `${userName} is in the waiting room`,
        MultipleOptionsMessageType.ADMIT_USER,
        optionsList,
        false,
      )
      .subscribe((res) => {
        if (!this.socialId) {
          this.error('Tried to manage user admit with undefined social');
          return;
        }

        if (res) {
          switch (res) {
            case AdmitOptions.ADMIT:
              void this.videoChatService.setAdmittedStatus(this.socialId, waitingUser.userId, true, false);
              break;
            case AdmitOptions.MANAGE:
              setTimeout(() => {
                this.sideSectionService.sideContainerType = SideContainerType.ACTION_MENU;
              }, 100);
              this.requestOpenGuestList$.next(true);
              break;
          }
          this.headerMultipleOptionsMessageService.removeMessage();
        }
      });
  }

  private sendMultipleAdmitUserMessage(waitingUsers: BaseUser[]): void {
    const optionsList: SettingsMultipleOptionsMessage[] = [
      { text: 'ADMIT ALL', color: '#39AD65' },
      { text: 'MANAGE', color: '#81B7E2' },
    ];

    this.headerMultipleOptionsMessageService
      .pushStaticMessage(
        `${waitingUsers.length} people are in the waiting room`,
        MultipleOptionsMessageType.MULTIPLE_ADMIT_USER,
        optionsList,
        false,
      )
      .subscribe((res) => {
        if (!this.socialId) {
          this.error('Tried to manage multiple user admit with undefined social');
          return;
        }

        if (res) {
          switch (res) {
            case AdmitOptions.ADMIT:
              void this.videoChatService.setAdmittedStatuses(
                this.socialId,
                waitingUsers.map(({ userId }) => userId),
                true,
                false,
              );
              break;
            case AdmitOptions.MANAGE:
              setTimeout(() => {
                this.sideSectionService.sideContainerType = SideContainerType.ACTION_MENU;
              }, 100);
              this.requestOpenGuestList$.next(true);
              break;
          }
          this.headerMultipleOptionsMessageService.removeMessage();
        }
      });
  }

  public addHost(myUserId: string, newHostId?: string, socialId?: string, firstName?: string, lastName?: string): void {
    // FIXME: is this necessary? Why can't we add a host when a toast or convo is active?
    if (this.event) {
      return;
    }
    void SocialStateMachine.shared.requestAddHost({
      userId: myUserId,
      hostId: newHostId ? newHostId : myUserId,
    });
    if (firstName) {
      this.displayHostInfo(firstName, lastName ?? '', 'is now a host.');
    }
    if (socialId && newHostId) {
      void this.videoChatService.addSocialHost(socialId, newHostId).toPromise();
    }
  }

  public didSomeoneMakeMeHost(message: Message): void {
    if (message.userId === this.userId) {
      this.displayHostInfo(message.firstName ?? '', message.lastName ?? '', message.message ?? '');

      // Only triggers the moderation feed history if this config is enabled
      if (this.moderatedChat && this.chatModerationSetting) {
        this.chatService.moderationChatHistory = true;
        this.requestPreviousChatMessages();
      }
    }
  }

  private requestPreviousChatMessages() {
    const todayInMs = new Date().getTime();
    const msg: ChatGetMessage = {
      t: OutgoingMessageType.CHATGET,
      beforeDate: todayInMs,
      maxCount: 20,
    };
    this.websocketService.sendMessage(msg);
  }
}
